// Daily fiscal report — fiscalized / storno / failed / pending invoices for one day, with one-click retry.
import React, { useEffect, useState } from 'react';
import { CalendarDays, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { apiCall } from './_shared';
import FiscalCell from './FiscalCell';
import CashDepositCard from './CashDepositCard';

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;
const today = () => new Date().toISOString().slice(0, 10);

const TONES = {
  slate: { box: 'bg-slate-50 border-slate-200', text: 'text-slate-700', head: 'bg-slate-50 text-slate-800' },
  emerald: { box: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', head: 'bg-emerald-50 text-emerald-800' },
  sky: { box: 'bg-sky-50 border-sky-200', text: 'text-sky-700', head: 'bg-sky-50 text-sky-800' },
  violet: { box: 'bg-violet-50 border-violet-200', text: 'text-violet-700', head: 'bg-violet-50 text-violet-800' },
  red: { box: 'bg-red-50 border-red-200', text: 'text-red-700', head: 'bg-red-50 text-red-800' },
  amber: { box: 'bg-amber-50 border-amber-200', text: 'text-amber-700', head: 'bg-amber-50 text-amber-800' },
};

const Kpi = ({ label, value, sub, tone = 'slate', testId }) => (
  <div className={`rounded-xl p-3 border ${TONES[tone].box}`} data-testid={testId}>
    <div className={`text-[10px] uppercase tracking-wide ${TONES[tone].text} font-semibold`}>{label}</div>
    <div className="text-xl font-bold text-slate-900">{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);

const InvoiceList = ({ title, rows, tone, userRole, onChanged, emptyText, testId }) => {
  const items = rows.map((r) => (
    <tr key={r.invoice_number} className="border-b last:border-0" data-testid={`${testId}-row-${r.invoice_number}`}>
      <td className="py-1.5 px-2 font-mono font-semibold">{r.invoice_number}{r.kind === 'storno' && <span className="ml-1 text-[10px] text-violet-700">STORNO of {r.storno_of}</span>}</td>
      <td className="py-1.5 px-2">{r.guest_name}</td>
      <td className="py-1.5 px-2 text-slate-500 text-xs">{(r.issued_at || '').slice(0, 10)}</td>
      <td className={`py-1.5 px-2 text-right font-semibold ${r.gross_total < 0 ? 'text-red-600' : ''}`}>{eur(r.fiscal?.total ?? r.gross_total)}</td>
      <td className="py-1.5 px-2 text-xs text-slate-500">{r.fiscal?.type_of_inv || '—'}{r.fiscal?.payment ? ` · ${r.fiscal.payment}` : ''}</td>
      <td className="py-1.5 px-2"><FiscalCell invoice={r} onChanged={onChanged} userRole={userRole} /></td>
    </tr>
  ));
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid={testId}>
      <div className={`px-3 py-2 text-sm font-semibold ${TONES[tone].head} border-b`}>{title} · {rows.length}</div>
      {rows.length ? (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-xs text-slate-500"><tr><th className="text-left py-1.5 px-2">Invoice</th><th className="text-left py-1.5 px-2">Guest</th><th className="text-left py-1.5 px-2">Issued</th><th className="text-right py-1.5 px-2">Total</th><th className="text-left py-1.5 px-2">Payment</th><th className="text-left py-1.5 px-2">Fiscal</th></tr></thead>
          <tbody>{items}</tbody>
        </table></div>
      ) : <div className="px-3 py-4 text-xs text-slate-400">{emptyText}</div>}
    </div>
  );
};

const FiscalDailyReport = ({ userRole }) => {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try { setData(await apiCall(`/api/admin/fiscal/report?date_str=${date}`)); } catch (e) { setMsg(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const retryAll = async () => {
    setRetrying(true); setMsg('');
    try {
      const r = await apiCall('/api/admin/fiscal/retry-failed', { method: 'POST' });
      setMsg(`Retried ${r.attempted}: ${r.fiscalized} fiscalized · ${r.failed} still failing`);
      await load();
    } catch (e) { setMsg(e.message); } finally { setRetrying(false); }
  };

  const s = data?.summary;
  return (
    <div className="space-y-4" data-testid="fiscal-day-tab">
      <div className="bg-white rounded-xl border p-3 flex flex-wrap items-center gap-2">
        <CalendarDays className="w-4 h-4 text-slate-500" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="fiscal-report-date" />
        <button type="button" onClick={load} className="px-3 py-1.5 rounded-lg border text-sm inline-flex items-center gap-1 hover:bg-slate-50" data-testid="fiscal-report-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
        {s?.failed > 0 && (
          <button type="button" onClick={retryAll} disabled={retrying} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-60" data-testid="fiscal-retry-all">
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Retry all failed ({s.failed})
          </button>
        )}
        {data && <span className="text-xs text-slate-500 ml-auto">EFI {data.env} {data.ready ? '· ready' : '· not configured'}</span>}
        {msg && <div className="w-full text-xs text-slate-700" data-testid="fiscal-report-msg">{msg}</div>}
      </div>
      {loading && !data ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div> : s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="fiscal-report-kpis">
            <Kpi label="Fiscalized" value={s.fiscalized} sub={eur(s.fiscalized_total)} tone="emerald" testId="kpi-fiscalized" />
            <Kpi label="Cash / Non-cash" value={eur(s.cash_total)} sub={`non-cash ${eur(s.noncash_total)}`} tone="sky" testId="kpi-cash" />
            <Kpi label="Storno" value={s.storno} sub={eur(s.storno_total)} tone="violet" testId="kpi-storno" />
            <Kpi label="Failed" value={s.failed} sub="all dates · needs retry" tone={s.failed ? 'red' : 'slate'} testId="kpi-failed" />
            <Kpi label="Not fiscalized" value={s.pending} sub={s.pending_older ? `+${s.pending_older} older` : 'issued this day'} tone={s.pending ? 'amber' : 'slate'} testId="kpi-pending" />
            <Kpi label="Cash deposit" value={s.deposit_registered ? eur(s.cash_deposit_initial) : '—'} sub={s.deposit_registered ? `withdrawn ${eur(s.cash_withdrawn)}` : 'not registered'} tone={s.deposit_registered ? 'emerald' : 'amber'} testId="kpi-deposit" />
          </div>
          <CashDepositCard onChanged={load} />
          <InvoiceList title="Failed at EFI" rows={data.failed} tone="red" userRole={userRole} onChanged={load} emptyText="No failed registrations." testId="fiscal-failed" />
          <InvoiceList title="Issued but not fiscalized" rows={data.pending} tone="amber" userRole={userRole} onChanged={load} emptyText="Every invoice issued this day is fiscalized." testId="fiscal-pending" />
          <InvoiceList title="Fiscalized" rows={[...data.fiscalized, ...data.storno]} tone="emerald" userRole={userRole} onChanged={load} emptyText="Nothing fiscalized on this day." testId="fiscal-done" />
        </>
      )}
    </div>
  );
};

export default FiscalDailyReport;
